import { Injectable } from '@angular/core';
import { Cluster } from '../models/cluster.model';
import { Subject, Subscription } from 'rxjs';
import { OriginalPortfolio } from '../models/original-portfolio.model';
import { ApiRequestsService } from './api-requests.service';
import { ListsService } from './lists.service';
import { CurrentPortfolio } from '../models/current-portfolio.model';
import { CurrentBenchmark } from '../models/current-benchmark.model';
import { CurrentStrategyTarget } from '../models/current-strategy-target.model';
import { AuthService } from './auth.service';
import { User } from '../models/user.model';
import { Watchlist } from '../models/watchlist.model';
import { NotificationsService } from './notifications.service';

@Injectable({
  providedIn: 'root'
})
export class ClustersService {

  user: User;
  userSubscription: Subscription;

  clusterList: Cluster[] = [];
  clusterListSubject = new Subject<Cluster[]>();
  currentCluster: Cluster;
  currentClusterSubject = new Subject<Cluster>();
  clusterLoading: boolean = false;
  clusterLoadingSubject = new Subject<boolean>();
  currentPortfolio: CurrentPortfolio;
  currentPortfolioSubject = new Subject<CurrentPortfolio>();
  currentOriginal: CurrentPortfolio;
  currentOriginalSubject = new Subject<CurrentPortfolio>();
  currentStrategy: CurrentStrategyTarget;
  currentStrategySubject = new Subject<CurrentStrategyTarget>();
  currentInvestable: CurrentPortfolio;
  currentInvestableSubject = new Subject<CurrentPortfolio>();
  currentProtected: CurrentPortfolio;
  currentProtectedSubject = new Subject<CurrentPortfolio>();
  currentBenchmark: CurrentBenchmark;
  currentBenchmarkSubject = new Subject<CurrentBenchmark>();

  watchlists: Watchlist[] = [];
  watchlistsSubject = new Subject<Watchlist[]>();

  constructor(private apiRequestsService: ApiRequestsService,
              private notificationsService: NotificationsService,
              private authService: AuthService,
              private listService: ListsService) {
    this.userSubscription = this.authService.userSubject.subscribe(
      (user: User) => {
        if (user) {
          if (!this.user || this.user.email!=user.email) {
            this.loadCurrentCluster(user);
            this.loadWatchlists();
          }
        }
        this.user = user;
      }
    );
    this.authService.emitUser();
  }

  async loadCurrentCluster(user:User) {
    await this.loadClusterList();
    const cluster_ids = this.clusterList.map((cluster: Cluster) => {
      return cluster.id;
    })
    if (user.current_cluster_id && cluster_ids.includes(user.current_cluster_id)) {
      this.select(user.current_cluster_id);
    }
  }

  emitClusterList() {
    // notify subscriptions and provide them with updated cluster list
    console.log('CALL ClustersService.emitClusterList()');
    this.clusterListSubject.next(this.clusterList);
  }

  emitClusterLoading() {
    // notify subscriptions and provide them with updated cluster
    console.log('CALL ClustersService.emitClusterLoading()');
    this.clusterLoadingSubject.next(this.clusterLoading);
  }

  emitCurrentCluster() {
    // notify subscriptions and provide them with updated cluster
    console.log('CALL ClustersService.emitCurrentCluster()');
    this.currentClusterSubject.next(this.currentCluster);
  }

  emitCurrentPortfolio() {
    // notify subscriptions and provide them with updated cluster
    console.log('CALL ClustersService.emitCurrentPortfolio()');
    this.currentPortfolioSubject.next(this.currentPortfolio);
  }

  emitCurrentOriginal() {
    // notify subscriptions and provide them with updated original portfolio
    console.log('CALL ClustersService.emitCurrentOriginal()');
    this.currentOriginalSubject.next(this.currentOriginal);
  }

  emitCurrentStrategy() {
    // notify subscriptions and provide them with updated strategy target
    console.log('CALL ClustersService.emitCurrentStrategy()');
    this.currentStrategySubject.next(this.currentStrategy);
  }

  emitCurrentInvestable() {
    // notify subscriptions and provide them with updated investable portfolio
    console.log('CALL ClustersService.emitCurrentInvestable()');
    this.currentInvestableSubject.next(this.currentInvestable);
  }

  emitCurrentProtected() {
    // notify subscriptions and provide them with updated protected portfolio
    console.log('CALL ClustersService.emitCurrentProtected()');
    this.currentProtectedSubject.next(this.currentProtected);
  }

  emitCurrentBenchmark() {
    // notify subscriptions and provide them with updated benchmark
    console.log('CALL ClustersService.emitCurrentBenchmark()');
    this.currentBenchmarkSubject.next(this.currentBenchmark);
  }

  emitWatchlists() {
    // notify subscriptions and provide them with updated watchlists
    console.log('CALL ClustersService.emitWatchlists()');
    this.watchlistsSubject.next(this.watchlists);
  }

  getCurrentCluster() {
    return this.currentCluster;
  }

  async loadClusterList() {
    const data: any = await this.apiRequestsService.get('cluster-list');
    if (data.status=='OK') {
      await this.listService.cleanData(data);

      this.clusterList = data.list.map(
        (data) => {
          this.listService.cleanDataBlocking(data);
          let cluster = new Cluster(data.id);
          cluster.initFromData(data);
          return cluster;
        }
      ) as Cluster[];
      this.emitClusterList();
    }
  }

  async select(id?: number) {
    if (this.currentCluster) {
      this.currentCluster = undefined;
      this.emitCurrentCluster();
    }

    if (id) {
      this.clusterLoading = true;
      this.emitClusterLoading();

      this.currentCluster = await this.loadCluster(id);
      this.emitCurrentCluster();

      if (this.currentCluster.has_original) {
        this.currentOriginal = await this.loadClusterOriginal(id);
      } else {
        this.currentOriginal = undefined;
      }
      this.emitCurrentOriginal();

      if (this.currentCluster.has_strategy) {
        this.currentStrategy = await this.loadClusterStrategy(id);
      } else {
        this.currentStrategy = undefined;
      }
      this.emitCurrentStrategy();

      if (this.currentCluster.has_investable) {
        this.currentInvestable = await this.loadClusterInvestable(id);
      } else {
        this.currentInvestable = undefined;
      }
      this.emitCurrentInvestable();

      if (this.currentCluster.has_protected) {
        this.currentProtected = await this.loadClusterProtected(id);
      } else {
        this.currentProtected = undefined;
      }
      this.emitCurrentProtected();

      if (!this.currentCluster.is_standalone) {
        this.currentPortfolio = this.currentOriginal;
      } else {
        this.currentPortfolio = this.currentInvestable;
      }
      this.emitCurrentPortfolio();

      this.currentBenchmark = await this.loadClusterBenchmark(id);
      this.emitCurrentBenchmark();

      this.clusterLoading = false;
      this.emitClusterLoading();
    } else {

      this.currentCluster = undefined;
      this.emitCurrentCluster();
      this.currentOriginal = undefined;
      this.emitCurrentOriginal();
      this.currentStrategy = undefined;
      this.emitCurrentStrategy();
      this.currentInvestable = undefined;
      this.emitCurrentInvestable();
      this.currentPortfolio = undefined;
      this.emitCurrentPortfolio();
      this.currentProtected = undefined;
      this.emitCurrentProtected();
      this.currentBenchmark = undefined;
      this.emitCurrentBenchmark();

    }
  }

  async loadCluster(id: number) {
    const data: any = await this.apiRequestsService.post('get-cluster',{id: id});

    if (data.status=='OK') {
      if (this.clusterList.length==0) {
        await this.loadClusterList();
      }

      let cluster = this.clusterList.filter((cluster) => {
        return cluster.id==id;
      })[0];

      await this.listService.cleanData(data.data);

      cluster.initFromData(data.data);
      return cluster;
    }
  }

  async deleteCluster() {
    if (this.currentCluster==undefined) {
      return;
    }

    try {
      const data: any = await this.apiRequestsService.post('delete-cluster',{id: this.currentCluster.id});

      if (data.status=='OK') {
        this.notificationsService.notify('cluster/delete-success');
        await this.select();

        await this.loadClusterList();
      }
    }
    catch (e) {
      this.notificationsService.notify('cluster/delete-failed');
    }
  }

  async loadClusterOriginal(cluster_id: number) {
    if (!this.currentCluster || this.currentCluster.id!=cluster_id) {
      await this.select(cluster_id);
    }

    const data: any = await this.apiRequestsService.post('get-original',{cluster_id: cluster_id});

    if (data.status=='OK') {
      await this.listService.cleanData(data.data);

      let original_portfolio = new CurrentPortfolio(cluster_id);
      original_portfolio.initFromData(data.data);
      return original_portfolio;
    }
  }

  async loadClusterStrategy(cluster_id: number) {
    if (!this.currentCluster || this.currentCluster.id!=cluster_id) {
      await this.select(cluster_id);
    }

    const data: any = await this.apiRequestsService.post('get-strategy',{cluster_id: cluster_id});

    if (data.status=='OK') {
      await this.listService.cleanData(data.data);

      let strategy = new CurrentStrategyTarget(cluster_id);
      strategy.initFromData(data.data);
      return strategy;
    }
  }

  async loadClusterInvestable(cluster_id: number) {
    if (!this.currentCluster || this.currentCluster.id!=cluster_id) {
      await this.select(cluster_id);
    }

    const data: any = await this.apiRequestsService.post('get-investable',{cluster_id: cluster_id});

    if (data.status=='OK') {
      await this.listService.cleanData(data.data);

      let investable_portfolio = new CurrentPortfolio(cluster_id);
      investable_portfolio.initFromData(data.data);
      return investable_portfolio;
    }
  }

  async loadClusterProtected(cluster_id: number) {
    if (!this.currentCluster || this.currentCluster.id!=cluster_id) {
      await this.select(cluster_id);
    }

    const data: any = await this.apiRequestsService.post('get-protected',{cluster_id: cluster_id});

    if (data.status=='OK') {
      await this.listService.cleanData(data.data);

      let protected_portfolio = new CurrentPortfolio(cluster_id);
      protected_portfolio.initFromData(data.data);
      return protected_portfolio;
    }
  }

  async loadClusterBenchmark(cluster_id: number) {
    if (!this.currentCluster || this.currentCluster.id!=cluster_id) {
      await this.select(cluster_id);
    }

    const data: any = await this.apiRequestsService.post('get-benchmark',{cluster_id: cluster_id});

    if (data.status=='OK') {
      await this.listService.cleanData(data.data);

      let benchmark = new CurrentBenchmark(cluster_id);
      benchmark.initFromData(data.data);
      return benchmark;
    }
  }

  async createOriginal(original: OriginalPortfolio) {
    const data: any = await this.apiRequestsService.post('create-original',original);

    if (data.status=='OK') {
      await this.loadClusterList();
      await this.select(data.cluster_id);
      this.createDefaultStrategies(data.cluster_id);
    }
  }

  async createStandalone(standalone: OriginalPortfolio) {
    const data: any = await this.apiRequestsService.post('create-standalone',standalone);

    if (data.status=='OK') {
      await this.loadClusterList();
      await this.select(data.cluster_id);
    }
    return data;
  }

  async createDefaultStrategies(id: number) {
    const data: any = await this.apiRequestsService.post('create-default-strategies',{id: id});

    if (data.status=='OK') {
      await this.select(id);
    }
    return data;
  }

  async updateCluster(update_data) {
    const data: any = await this.apiRequestsService.post('update-cluster',{cluster_id: this.currentCluster.id, ...update_data});

    if (data.status=='OK') {
      await this.select(this.currentCluster.id);
    }
    return data;
  }

  async createWatchlist(watchlist: Watchlist) {
    try {
      const data: any = await this.apiRequestsService.post('create-watchlist',watchlist);

      if (data.status=='OK') {
        await this.loadWatchlists();
        this.notificationsService.notify('watchlist/create-success');
      }
    }
    catch (e) {
      this.notificationsService.notify('watchlist/create-failed');
      throw(e);
    }
  }

  async loadWatchlists() {
    const data: any = await this.apiRequestsService.get('list-watchlist',true);

    if (data.status=='OK') {
      this.watchlists = data.list as Watchlist[];
      this.emitWatchlists();
    }
  }

  async deleteWatchlist(watchlist_id: number) {
    try {
      const data: any = await this.apiRequestsService.post('delete-watchlist',{id: watchlist_id});

      if (data.status=='OK') {
        await this.loadWatchlists();
        this.notificationsService.notify('watchlist/delete-success');
      }
    }
    catch(e) {
      this.notificationsService.notify('watchlist/delete-failed');
      throw(e);
    }
  }

  async viewReport(cluster_id: number) {
    try {
      const data: any = await this.apiRequestsService.post('cluster-report',{cluster_id: cluster_id});

      this.apiRequestsService.open('cluster-report',{cluster_id: cluster_id});
    }
    catch(e) {
      //this.notificationsService.notify('watchlist/delete-failed');
      throw(e);
    }
  }

}
